import { useEffect, useRef, useState } from 'react';

import { Button, ButtonGroup, Heading, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';
import { getClient as getDefaultClient } from '@zg-rentals/ts-client-http-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_setAsPropertyOnly } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { getClient as getRentalManagerApiClient } from '@zg-rentals/ts-rental-manager-api-client/client';
import { DatarouterError } from '@zg-rentals/web-base';

import { useConfirmProperty } from '../../hooks';
import { useAddPropertyDetailsForm } from '../../hooks';
import { contextUtils, formatAddress } from '../../utils';

import { CREATE_LISTING_MUTATION_INVALIDATES, PROPERTY_TYPES, type Step, STEPS } from '../../constants';

import {
  emitOpenEvent as emitAddPropertyModalImpression,
  emitGoBackAndEditAddressEvent,
} from '../../analytics/events/addPropertyModal';
import AddPropertyDetailsModal from './steps/AddPropertyDetailsModal';

import {
  emitMultiplexConfirmAddressClickEvent,
  emitMultiplexConfirmUnitEntryClickEvent,
} from '../../analytics/events/multiplex';
import PropertyAddedSuccessStep from './steps/PropertyAddedSuccessStep';
import PropertyDetailsStep from './steps/PropertyDetailsStep';
import ReviewPropertyInfoStep from './steps/ReviewPropertyInfoStep';
import UnitEntryStep from './steps/UnitEntryStep';

import type { ReactNode } from 'react';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type {
  RentalManagerAuthCheckUserInfoWebApiDto,
  UserPropertyEditSetAsPropertyOnlyV2RequestDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { CreateMultiplexResult } from '../../hooks';
import type { AddPropertyDetailsState } from './steps/AddPropertyDetailsModal';

const { COMMUNITY, MULTIPLEX } = PROPERTY_TYPES;

type PropertyDetailsStepState = {
  floorPlanId: string;
  beds?: number;
  baths?: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (listingAlias: string) => void;
  /** A multiplex passes every unit's alias, since any one of them may represent the building's group. */
  onPropertyAdded?: (listingAliases: Array<string>) => void;
  /** IA test B collects room for rent in its own step, so it suppresses the checkbox here. */
  showRoomForRent?: boolean;
  /** Replaces the intro paragraph on the first step. Defaults to the listing copy. */
  introText?: ReactNode;
  isScreenLgGTE: boolean;
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  /** A full URL, a path with a query, or a search string. Next hosts can pass `router.asPath`. */
  queryParams: string;
};

/**
 * Before this renders, the host app must register its rental-manager-api client with `setClient` from
 * `@zg-rentals/ts-rental-manager-api-client/client`. Every call in this flow, including the tenant modal,
 * uses that client. If no client is registered, the calls use the generic default client from
 * `@zg-rentals/ts-client-http-client`, which can point at the wrong backend.
 */
const AddPropertyFlowModal = ({
  isOpen,
  onClose,
  showRoomForRent = true,
  onConfirm = () => undefined,
  onPropertyAdded,
  introText,
  isScreenLgGTE,
  user,
  queryParams,
}: Props): JSX.Element => {
  const triggerObject = contextUtils.isRedirectToPayments(queryParams) ? 'start_collecting_rent_button' : undefined;
  const { enqueueToast } = useToast();
  const [step, setStep] = useState<Step>(STEPS.DETAILS);
  const [detailsState, setDetailsState] = useState<AddPropertyDetailsState | null>(null);
  const [propertyDetailsStepState, setPropertyDetailsStepState] = useState<PropertyDetailsStepState | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [multiplexBuildingSessionId, setMultiplexBuildingSessionId] = useState<string | null>(null);
  const [firstUnitListingAlias, setFirstUnitListingAlias] = useState<string | null>(null);
  const [tenantModalListingAlias, setTenantModalListingAlias] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { mutateAsync: setAsPropertyOnly, isPending: isSettingPropertyOnly } =
    useRentalManagerUserPropertyEditV2WebApiHandler_setAsPropertyOnly({
      meta: {
        invalidates: [...CREATE_LISTING_MUTATION_INVALIDATES],
        context: { api: 'setAsPropertyOnly', details: 'AddPropertyFlowModal' },
      },
    });

  // Awaited because the create listing picker only lists property-only properties. A failure is
  // already logged by the shared MutationCache, and the property exists either way, so carry on.
  const markAsPropertyOnly = (postData: UserPropertyEditSetAsPropertyOnlyV2RequestDto) =>
    setAsPropertyOnly({ postData }).catch(() => undefined);

  const handleDetailsContinue = (state: AddPropertyDetailsState) => {
    setDetailsState(state);
    setStep(STEPS.REVIEW);
  };

  const {
    address,
    propertyType,
    unit,
    numUnits,
    addressError,
    propertyTypeError,
    numUnitsError,
    propertyAlreadyPostedWarningMessage,
    isStreetEasyRestricted,
    unitAlert,
    isVerifyingAddress,
    isPropertyTypeDisabled,
    isRoomForRent,
    isRoomForRentEligible,
    toggleRoomForRent,
    addressInputRef,
    addressDetails,
    queryData,
    handleAddressChange,
    handleSelectAddress,
    handlePropertyTypeChange,
    handleUnitChange,
    handleNumUnitsChange,
    handleContinue,
  } = useAddPropertyDetailsForm({
    onContinue: handleDetailsContinue,
    user,
  });

  const { confirmProperty } = useConfirmProperty({
    user: user ?? undefined,
    onListingAliasSet: (alias) => setDetailsState((prev) => (prev ? { ...prev, listingAlias: alias } : null)),
  });

  const reviewScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (step === STEPS.REVIEW) {
      reviewScrollRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [step]);

  useEffect(() => {
    if (isOpen) {
      emitAddPropertyModalImpression({ user: user as unknown as ZrmClickstreamUser, triggerObject });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    emitGoBackAndEditAddressEvent({ user: user ?? null });
    setStep(STEPS.DETAILS);
  };

  const handleReviewConfirm = async () => {
    if (detailsState?.propertyType === MULTIPLEX) {
      emitMultiplexConfirmAddressClickEvent({ user: user ?? null });
      setStep(STEPS.UNIT_ENTRY);
      return;
    }

    if (!detailsState) return;

    const { formattedAddressWithoutUnit } = formatAddress(detailsState.address);
    const isSingleFamily = detailsState.propertyType !== COMMUNITY;

    setIsConfirming(true);
    try {
      const result = await confirmProperty({
        isRoomForRent: detailsState.isRoomForRent,
        propertyType: detailsState.propertyType,
        fullAddress: formattedAddressWithoutUnit,
        unit: detailsState.address.unit,
        numUnits: detailsState.numUnits,
      });
      const listingAlias = result?.responseData?.listingAlias;
      if (listingAlias && !detailsState.isRoomForRent) await markAsPropertyOnly({ listingAlias });
      if (isSingleFamily) {
        const fpId = result?.responseData?.floorPlans?.[0]?.id;
        if (fpId) {
          setPropertyDetailsStepState({
            floorPlanId: fpId,
            beds: addressDetails?.beds,
            baths: addressDetails?.baths,
          });
          setStep(STEPS.PROPERTY_DETAILS);
          return;
        }
        logError({
          error: new Error('Missing floorPlanId after confirmProperty'),
          context: 'AddPropertyFlowModal.handleReviewConfirm',
        });
      }
      if (onPropertyAdded) {
        if (listingAlias) onPropertyAdded([listingAlias]);
        handleClose();
      } else {
        setStep(STEPS.SUCCESS);
      }
    } catch (err) {
      logError({ error: err, context: 'AddPropertyFlowModal.handleReviewConfirm' });
      enqueueToast(
        <Toast tone="critical">
          {DatarouterError.getDisplayMessage(err, 'Property couldn’t be added. Try again.')}
        </Toast>,
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const handlePropertyDetailsConfirm = () => {
    if (onPropertyAdded) {
      if (detailsState?.listingAlias) onPropertyAdded([detailsState.listingAlias]);
      handleClose();
      return;
    }
    setStep(STEPS.SUCCESS);
  };

  const handleUnitEntryConfirm = async ({
    multiplexBuildingSessionId: sessionId,
    successfulUnits,
  }: CreateMultiplexResult) => {
    const firstAlias = successfulUnits[0]?.listingAlias ?? null;
    setFirstUnitListingAlias(firstAlias);
    setMultiplexBuildingSessionId(successfulUnits.length > 1 ? sessionId : null);
    emitMultiplexConfirmUnitEntryClickEvent({
      currentProperty: { listingAlias: successfulUnits.map((u) => u.listingAlias).join(',') },
      user: user ?? null,
    });
    if (firstAlias) {
      await markAsPropertyOnly({
        listingAlias: firstAlias,
        multiplexBuildingSessionId: successfulUnits.length > 1 ? sessionId : undefined,
      });
    }
    if (onPropertyAdded) {
      if (firstAlias) onPropertyAdded(successfulUnits.map((u) => u.listingAlias));
      handleClose();
      return;
    }
    setStep(STEPS.SUCCESS);
  };

  const handleSuccessClose = () => {
    onConfirm(firstUnitListingAlias || detailsState!.listingAlias);
    handleClose();
  };

  const handleTenantModalClose = () => {
    setTenantModalListingAlias(null);
    handleClose();
  };

  return (
    <>
      <Modal
        open={isOpen && (step === STEPS.DETAILS || step === STEPS.REVIEW)}
        onOpenChange={(open) => !open && handleClose()}
        size={{ base: 'fullScreen', md: 'lg' }}
        useDismissProps={{ outsidePress: false }}
        header={<Heading level={2}>Add property</Heading>}
        body={
          <>
            {step === STEPS.DETAILS && (
              <AddPropertyDetailsModal
                address={address}
                isRoomForRent={isRoomForRent}
                isRoomForRentEligible={isRoomForRentEligible}
                showRoomForRent={showRoomForRent}
                toggleRoomForRent={toggleRoomForRent}
                propertyType={propertyType}
                unit={unit}
                numUnits={numUnits}
                addressError={addressError}
                propertyTypeError={propertyTypeError}
                numUnitsError={numUnitsError}
                propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
                isStreetEasyRestricted={isStreetEasyRestricted}
                introText={introText}
                unitAlert={unitAlert}
                isPropertyTypeDisabled={isPropertyTypeDisabled}
                addressInputRef={addressInputRef}
                queryData={queryData}
                handleAddressChange={handleAddressChange}
                handleSelectAddress={handleSelectAddress}
                handlePropertyTypeChange={handlePropertyTypeChange}
                handleUnitChange={handleUnitChange}
                handleNumUnitsChange={handleNumUnitsChange}
              />
            )}
            {detailsState && (
              <div ref={reviewScrollRef} style={{ display: step === STEPS.REVIEW ? undefined : 'none' }}>
                <ReviewPropertyInfoStep
                  address={detailsState.address}
                  propertyType={detailsState.propertyType}
                  numUnits={detailsState.numUnits}
                  showImpreciseLocationAlert={detailsState.isImpreciseLocation}
                  isScreenLgGTE={isScreenLgGTE}
                  user={user}
                />
              </div>
            )}
          </>
        }
        footer={
          step === STEPS.DETAILS ? (
            <ButtonGroup aria-label="Action buttons">
              <TextButton tone="brand" onClick={handleClose}>
                Cancel
              </TextButton>
              <Button
                data-testid="flow-continue-button"
                fluid={false}
                tone="brand"
                emphasis="filled"
                loading={isVerifyingAddress}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </ButtonGroup>
          ) : (
            <ButtonGroup aria-label="Review property info" css={{ justifyContent: 'space-between', width: '100%' }}>
              <Button
                fluid={false}
                tone="brand"
                emphasis="bare"
                icon={<IconChevronLeftFilled />}
                iconPosition="start"
                disabled={isConfirming}
                onClick={handleBack}
              >
                Edit property info
              </Button>
              <Button
                data-testid="review-confirm-button"
                fluid={false}
                tone="brand"
                emphasis="filled"
                loading={isConfirming}
                onClick={handleReviewConfirm}
              >
                Confirm
              </Button>
            </ButtonGroup>
          )
        }
      />
      {detailsState && (
        <UnitEntryStep
          isOpen={isOpen && step === STEPS.UNIT_ENTRY}
          fullAddress={formatAddress(detailsState.address).formattedAddressWithoutUnit}
          numUnits={detailsState.numUnits}
          isSubmitting={isSettingPropertyOnly}
          onClose={handleClose}
          onConfirm={handleUnitEntryConfirm}
        />
      )}
      {detailsState && propertyDetailsStepState && (
        <PropertyDetailsStep
          isOpen={isOpen && step === STEPS.PROPERTY_DETAILS}
          floorPlanId={propertyDetailsStepState.floorPlanId}
          initialBeds={propertyDetailsStepState.beds}
          initialBaths={propertyDetailsStepState.baths}
          isStreetEasyRestricted={isStreetEasyRestricted}
          onClose={handleClose}
          onConfirm={handlePropertyDetailsConfirm}
        />
      )}
      {detailsState && (
        <PropertyAddedSuccessStep
          isOpen={isOpen && step === STEPS.SUCCESS && !tenantModalListingAlias}
          {...detailsState}
          multiplexBuildingSessionId={multiplexBuildingSessionId ?? undefined}
          firstUnitListingAlias={firstUnitListingAlias ?? undefined}
          onClose={handleSuccessClose}
          onAddTenantClick={setTenantModalListingAlias}
          user={user}
        />
      )}
      {tenantModalListingAlias && (
        <ModalTenantSelect
          client={getRentalManagerApiClient() ?? getDefaultClient()}
          listingAlias={tenantModalListingAlias}
          isOpen={isOpen}
          queryClient={queryClient}
          onClose={handleTenantModalClose}
        />
      )}
    </>
  );
};

export default AddPropertyFlowModal;
